import {
  Alert,
  Box,
  Card,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatCurrency } from "@/utils/format";
import { LinkProduto } from "@/components/panel/LinkProduto";
import { getRupturaProdutoDetalhe, resolveEmpresaRef, type RupturaProduto } from "@/api/ruptura.service";
import { formatData, formatNumero, ProblemaChip, Voltar, type NomeLoja } from "./rupturaComum";
import { useRupturaBusca } from "./useRupturaBusca";

// Detalhe de um produto da ruptura: situação, estoque, cadastro, pedidos e histórico.

const cabecalhoSx = {
  fontWeight: 700,
  color: "grey.400",
  textTransform: "uppercase",
  fontSize: "0.7rem",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
} as const;

function Campo({ label, valor }: { label: string; valor: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </Typography>
      <Typography fontWeight={600} noWrap title={valor}>
        {valor}
      </Typography>
    </Box>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {titulo}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>
        {children}
      </Box>
    </Box>
  );
}

export function DetalheView({
  codProduto,
  codEmpresa,
  produto,
  clientId,
  nomeLoja,
  onVoltar,
}: {
  codProduto: string;
  codEmpresa: string;
  // Linha da lista, mostrada enquanto o detalhe carrega. Reaberto pela URL não há.
  produto?: RupturaProduto;
  clientId: string;
  nomeLoja: NomeLoja;
  onVoltar: () => void;
}) {
  const {
    dados: detalhe,
    erro,
    loading,
  } = useRupturaBusca(
    `detalhe:${clientId}:${codEmpresa}:${codProduto}`,
    () => getRupturaProdutoDetalhe(codProduto, codEmpresa, clientId),
    "Não foi possível carregar o produto.",
  );

  const d = detalhe ?? produto;
  if (!d) {
    return (
      <Stack gap={2}>
        <Voltar label="Voltar aos produtos" onClick={onVoltar} />
        {loading && <LinearProgress />}
        {erro && <Alert severity="error">{erro}</Alert>}
        {!loading && !erro && (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
            Produto não encontrado.
          </Typography>
        )}
      </Stack>
    );
  }
  const solucao = d.solucao ? resolveEmpresaRef(d.solucao, d.cod_empresa_ref ? nomeLoja(d.cod_empresa_ref) : null) : "";

  return (
    <Stack gap={2}>
      <Voltar label="Voltar aos produtos" onClick={onVoltar} />
      {loading && <LinearProgress />}
      {erro && <Alert severity="error">{erro}</Alert>}

      <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack gap={3}>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ textTransform: "uppercase" }}>
              {d.descricao}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cód. <LinkProduto codProduto={d.cod_produto} codEmpresa={d.cod_empresa} /> · {nomeLoja(d.cod_empresa)}
            </Typography>
          </Box>

          <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.subtle" }}>
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: solucao || d.acao_sugerida ? 1 : 0 }}>
              <ProblemaChip codProblema={d.cod_problema} nome={d.problema} />
              {d.cod_solucao !== 0 && <Chip size="small" variant="outlined" label={d.responsavel} />}
              {d.dias_entrega_atrasado ? (
                <Typography variant="body2" color="text.secondary">
                  {d.dias_entrega_atrasado} dia(s) desde o pedido
                </Typography>
              ) : null}
            </Stack>
            {solucao && <Typography fontWeight={600}>{solucao}</Typography>}
            {d.acao_sugerida && (
              <Typography variant="body2" color="text.secondary">
                {d.acao_sugerida}
              </Typography>
            )}
          </Box>

          <Secao titulo="Estoque e abastecimento">
            <Campo label="Estoque disponível" valor={formatNumero(d.estoque_disponivel)} />
            <Campo label="Cobertura (dias)" valor={formatNumero(d.cobertura, 1)} />
            <Campo label="Média de venda diária" valor={formatNumero(d.mdv)} />
            <Campo label="Forma de abastecimento" valor={d.forma_abastecimento ?? "—"} />
            <Campo label="Estoque mínimo" valor={formatNumero(d.qt_min)} />
            <Campo label="Estoque máximo" valor={formatNumero(d.qt_max)} />
            <Campo label="Pedido pendente" valor={formatNumero(d.qtd_pend_ped_compra)} />
            <Campo label="Custo" valor={formatCurrency(d.custo)} />
            <Campo label="Estimativa de Perda" valor={d.valor_risco === null ? "—" : formatCurrency(d.valor_risco)} />
          </Secao>

          {detalhe && (
            <>
              <Secao titulo="Produto">
                <Campo label="EAN" valor={detalhe.ean ?? "—"} />
                <Campo label="Categoria" valor={detalhe.categoria ?? "—"} />
                <Campo label="Seção" valor={detalhe.secao ?? "—"} />
                <Campo label="Grupo" valor={detalhe.grupo ?? "—"} />
                <Campo label="Subgrupo" valor={detalhe.subgrupo ?? "—"} />
                <Campo label="Fornecedor" valor={detalhe.fornecedor} />
                <Campo label="Comprador" valor={detalhe.nome_comprador} />
              </Secao>

              <Secao titulo="Pedidos e movimento">
                <Campo label="Pedido mais antigo" valor={formatData(detalhe.dt_ped_menor)} />
                <Campo label="Pedido mais recente" valor={formatData(detalhe.dt_ped_maior)} />
                <Campo label="Última entrada" valor={formatData(detalhe.data_ult_entrada)} />
                <Campo label="Última saída" valor={formatData(detalhe.data_ult_saida)} />
              </Secao>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Histórico de problemas
                </Typography>
                {detalhe.historico.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sem histórico para este produto.
                  </Typography>
                ) : (
                  <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "background.subtle" }}>
                          <TableCell sx={cabecalhoSx}>Início</TableCell>
                          <TableCell sx={cabecalhoSx}>Fim</TableCell>
                          <TableCell sx={cabecalhoSx}>Problema</TableCell>
                          <TableCell sx={cabecalhoSx}>Solução</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detalhe.historico.map((h, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{formatData(h.dt_inicio)}</TableCell>
                            <TableCell>{h.dt_fim ? formatData(h.dt_fim) : "Atual"}</TableCell>
                            <TableCell>
                              <ProblemaChip codProblema={h.cod_problema} nome={h.problema} />
                            </TableCell>
                            <TableCell>{h.solucao ? resolveEmpresaRef(h.solucao, null) : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
